import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModalArchivo from './ModalArchivo'
import { archivosService, type Archivo as ArchivoAPI, type FiltrosArchivos as FiltrosAPI, type EstadisticasArchivos as EstadisticasAPI } from '../../../services/archivosService'
import { resolverPresentacionErrorApi } from '../../../services/apiError'
import { useToast } from '../../../ganchos/interfaz-usuario/usarNotificaciones'
import { usePermisos } from '../../../ganchos/usePermisos'
import {
  DocumentIcon,
  
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  ArchiveBoxIcon,
  
  
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  Squares2X2Icon,
  ListBulletIcon,
  
  CheckCircleIcon
} from '@heroicons/react/24/outline'

// Interfaces locales extendidas
interface Archivo extends ArchivoAPI {
  extension?: string
  tags?: string[]
  descargas?: number
  thumbnail?: string
  nivelAcceso?: 'publico' | 'privado' | 'restringido'
  version?: string
}

interface FiltrosArchivos extends Partial<FiltrosAPI> {
  nivelAcceso?: string
  fechaDesde?: string
  fechaHasta?: string
  tamaño?: string
}

interface EstadisticasArchivos extends EstadisticasAPI {
  descargasHoy?: number
}

const PaginaGestionArchivos: React.FC = () => {
  const { showToast } = useToast()
  const [archivos, setArchivos] = useState<Archivo[]>([])
  const [archivosFiltrados, setArchivosFiltrados] = useState<Archivo[]>([])
  const [cargando, setCargando] = useState(true)
  const [estadisticas, setEstadisticas] = useState<EstadisticasArchivos>({
    total: 0,
    porTipo: {},
    porCategoria: {},
    tamañoTotal: 0,
    subidosHoy: 0,
    subidosEsteMes: 0,
    descargasHoy: 0
  })
  const [filtros, setFiltros] = useState<FiltrosArchivos>({
    busqueda: '',
    tipo: '',
    categoria: '',
    estado: '',
    nivelAcceso: '',
    fechaInicio: '',
    fechaFin: '',
    tamaño: ''
  })
  const [ordenamiento, setOrdenamiento] = useState<{campo: string, direccion: 'asc' | 'desc'}>({
    campo: 'fechaSubida',
    direccion: 'desc'
  })
  const [vistaActual, setVistaActual] = useState<'grid' | 'lista'>('grid')
  const [paginaActual, setPaginaActual] = useState(1)
  const [archivosPorPagina] = useState(12)
  const [mostrarFiltros, setMostrarFiltros] = useState(false)
  const [archivoSeleccionado, setArchivoSeleccionado] = useState<Archivo | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [tipoModal, setTipoModal] = useState<'ver' | 'editar' | 'eliminar' | 'subir'>('ver')
  const [archivosSeleccionados, setArchivosSeleccionados] = useState<string[]>([])
  const [mostrarSubida, setMostrarSubida] = useState(false)
  const { tienePermiso } = usePermisos()

  // Cargar datos desde el backend
  useEffect(() => {
    const cargarDatos = async () => {
      setCargando(true)
      try {
        // Cargar archivos y estadísticas en paralelo
        const [archivosData, estadisticasData] = await Promise.all([
          archivosService.obtenerArchivos(),
          archivosService.obtenerEstadisticas()
        ])
        
        setArchivos(archivosData)
        setArchivosFiltrados(archivosData)
        setEstadisticas(estadisticasData)
      } catch (error) {
        console.error('Error al cargar datos:', error)
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error',
          mensajeFallback: 'No fue posible cargar los archivos del sistema.',
          titulosPorCodigo: {
            forbidden: 'Acceso denegado',
            unauthorized: 'Sesión no válida',
          },
        })
        showToast({
          type: 'error',
          title: presentacion.title,
          message: presentacion.message,
        })
        // En caso de error, usar datos vacíos
        setArchivos([])
        setArchivosFiltrados([])
        setEstadisticas({
          total: 0,
          porTipo: {},
          porCategoria: {},
          tamañoTotal: 0,
          subidosHoy: 0,
          subidosEsteMes: 0,
          descargasHoy: 0
        })
      } finally {
        setCargando(false)
      }
    }

    cargarDatos()
  }, [])

  // Aplicar filtros y cargar datos filtrados desde el backend
  useEffect(() => {
    const aplicarFiltros = async () => {
      if (archivos.length === 0) return // No aplicar filtros si no hay datos cargados

      try {
        // Si hay filtros activos, hacer una nueva consulta al backend
        const hayFiltrosActivos = Object.values(filtros).some(valor => valor && valor.trim() !== '')
        
        if (hayFiltrosActivos) {
          const filtrosAPI: FiltrosAPI = {
            busqueda: filtros.busqueda || undefined,
            tipo: filtros.tipo || undefined,
            categoria: filtros.categoria || undefined,
            estado: filtros.estado || undefined,
            fechaInicio: filtros.fechaInicio || undefined,
            fechaFin: filtros.fechaFin || undefined,
            usuario: filtros.usuario || undefined,
          }
          
          const archivosFiltrados = await archivosService.obtenerArchivos(filtrosAPI)
          setArchivosFiltrados(archivosFiltrados)
        } else {
          // Si no hay filtros, usar todos los archivos
          setArchivosFiltrados([...archivos])
        }
      } catch (error) {
        console.error('Error al aplicar filtros:', error)
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error',
          mensajeFallback: 'No fue posible aplicar los filtros de archivos.',
        })
        showToast({
          type: 'error',
          title: presentacion.title,
          message: presentacion.message,
        })
        // En caso de error, filtrar localmente
        let archivosFiltrados = [...archivos]

        if (filtros.busqueda) {
          archivosFiltrados = archivosFiltrados.filter(archivo =>
            archivo.nombre.toLowerCase().includes(filtros.busqueda!.toLowerCase()) ||
            archivo.descripcion?.toLowerCase().includes(filtros.busqueda!.toLowerCase()) ||
            archivo.tags?.some(tag => tag.toLowerCase().includes(filtros.busqueda!.toLowerCase()))
          )
        }

        if (filtros.tipo) {
          archivosFiltrados = archivosFiltrados.filter(archivo => archivo.tipo === filtros.tipo)
        }

        if (filtros.categoria) {
          archivosFiltrados = archivosFiltrados.filter(archivo => archivo.categoria === filtros.categoria)
        }

        if (filtros.estado) {
          archivosFiltrados = archivosFiltrados.filter(archivo => archivo.estado === filtros.estado)
        }

        setArchivosFiltrados(archivosFiltrados)
      }
      
      setPaginaActual(1)
    }

    aplicarFiltros()
  }, [filtros, archivos])

  // Aplicar ordenamiento local
  useEffect(() => {
    const archivosOrdenados = [...archivosFiltrados].sort((a, b) => {
      const valorA = a[ordenamiento.campo as keyof Archivo] as string | number
      const valorB = b[ordenamiento.campo as keyof Archivo] as string | number
      
      if (typeof valorA === 'string' && typeof valorB === 'string') {
        return ordenamiento.direccion === 'asc' 
          ? valorA.localeCompare(valorB)
          : valorB.localeCompare(valorA)
      } else {
        return ordenamiento.direccion === 'asc' 
          ? (valorA as number) - (valorB as number)
          : (valorB as number) - (valorA as number)
      }
    })

    setArchivosFiltrados(archivosOrdenados)
  }, [ordenamiento])

  // Paginación
  const indiceInicio = (paginaActual - 1) * archivosPorPagina
  const indiceFin = indiceInicio + archivosPorPagina
  const archivosPaginados = archivosFiltrados.slice(indiceInicio, indiceFin)
  const totalPaginas = Math.ceil(archivosFiltrados.length / archivosPorPagina)

  const formatearTamaño = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const obtenerIconoTipo = (tipo: string) => {
    switch (tipo) {
      case 'imagen':
        return <PhotoIcon className="h-8 w-8 text-green-500" />
      case 'video':
        return <VideoCameraIcon className="h-8 w-8 text-red-500" />
      case 'audio':
        return <MusicalNoteIcon className="h-8 w-8 text-purple-500" />
      case 'documento':
        return <DocumentTextIcon className="h-8 w-8 text-blue-500" />
      default:
        return <DocumentIcon className="h-8 w-8 text-gray-500" />
    }
  }

  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'activo':
        return 'bg-green-100 text-green-800'
      case 'archivado':
        return 'bg-yellow-100 text-yellow-800'
      case 'eliminado':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  

  const manejarOrdenamiento = (campo: string) => {
    setOrdenamiento(prev => ({
      campo,
      direccion: prev.campo === campo && prev.direccion === 'asc' ? 'desc' : 'asc'
    }))
  }

  const manejarSeleccionArchivo = (id: string) => {
    setArchivosSeleccionados(prev => 
      prev.includes(id) 
        ? prev.filter(archivoId => archivoId !== id)
        : [...prev, id]
    )
  }

  const manejarSeleccionTodos = () => {
    if (archivosSeleccionados.length === archivosPaginados.length) {
      setArchivosSeleccionados([])
    } else {
      setArchivosSeleccionados(archivosPaginados.map(archivo => archivo.id))
    }
  }

  const abrirModal = (tipo: typeof tipoModal, archivo?: Archivo) => {
    setTipoModal(tipo)
    setArchivoSeleccionado(archivo || null)
    setModalVisible(true)
  }

  const cerrarModal = () => {
    setModalVisible(false)
    setArchivoSeleccionado(null)
  }

  const manejarGuardarArchivo = async (datosArchivo: Partial<Archivo>) => {
    try {
      if (archivoSeleccionado) {
        // Editar archivo existente usando el servicio
        const archivoActualizado = await archivosService.actualizarArchivo(archivoSeleccionado.id, datosArchivo)
        
        // Actualizar la lista local
        const archivosActualizados = archivos.map(archivo =>
          archivo.id === archivoSeleccionado.id ? archivoActualizado : archivo
        )
        setArchivos(archivosActualizados)
        
        // Recargar estadísticas
        const nuevasEstadisticas = await archivosService.obtenerEstadisticas()
        setEstadisticas(nuevasEstadisticas)
        showToast({
          type: 'success',
          title: 'Archivo actualizado',
          message: 'El archivo se actualizó correctamente.',
        })
      }
    } catch (error) {
      console.error('Error al guardar archivo:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'No fue posible guardar el archivo.',
        titulosPorCodigo: {
          validation_error: 'Datos inválidos',
          forbidden: 'Acceso denegado',
          not_found: 'Archivo no encontrado',
        },
        mensajesPorCodigo: {
          validation_error: 'Verifica la información capturada antes de guardar el archivo.',
          forbidden: 'No tienes permisos para editar archivos.',
          not_found: 'El archivo que intentas editar ya no existe.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    }
  }

  const manejarEliminarArchivo = async (id: string) => {
    try {
      // Eliminar archivo usando el servicio
      await archivosService.eliminarArchivo(id)
      
      // Actualizar la lista local
      const archivosActualizados = archivos.filter(archivo => archivo.id !== id)
      setArchivos(archivosActualizados)
      setArchivosSeleccionados(prev => prev.filter(archivoId => archivoId !== id))
      
      // Recargar estadísticas
      const nuevasEstadisticas = await archivosService.obtenerEstadisticas()
      setEstadisticas(nuevasEstadisticas)
      showToast({
        type: 'success',
        title: 'Archivo eliminado',
        message: 'El archivo se eliminó correctamente.',
      })
    } catch (error) {
      console.error('Error al eliminar archivo:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'No fue posible eliminar el archivo.',
        titulosPorCodigo: {
          forbidden: 'Acceso denegado',
          not_found: 'Archivo no encontrado',
          conflict: 'Operación no permitida',
        },
        mensajesPorCodigo: {
          forbidden: 'No tienes permisos para eliminar archivos.',
          not_found: 'El archivo que intentas eliminar ya no existe.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    }
  }

  const manejarSubirArchivo = async (archivo: File, metadatos: Partial<Archivo>) => {
    try {
      // Subir archivo usando el servicio
      const nuevoArchivo = await archivosService.subirArchivo(archivo, metadatos)
      
      // Actualizar la lista local
      setArchivos(prev => [nuevoArchivo, ...prev])
      
      // Recargar estadísticas
      const nuevasEstadisticas = await archivosService.obtenerEstadisticas()
      setEstadisticas(nuevasEstadisticas)
      showToast({
        type: 'success',
        title: 'Archivo subido',
        message: 'El archivo se subió correctamente.',
      })
    } catch (error) {
      console.error('Error al subir archivo:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'No fue posible subir el archivo.',
        titulosPorCodigo: {
          validation_error: 'Datos inválidos',
          forbidden: 'Acceso denegado',
          conflict: 'Archivo duplicado',
        },
        mensajesPorCodigo: {
          validation_error: 'Verifica el archivo y los metadatos antes de intentar subirlo.',
          forbidden: 'No tienes permisos para subir archivos.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message,
      })
    }
  }

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestión de Archivos</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Administra documentos, imágenes y archivos del sistema
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex space-x-3">
              <button
                onClick={() => setMostrarFiltros(!mostrarFiltros)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filtros
              </button>
              <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
                <button
                  onClick={() => setVistaActual('grid')}
                  aria-label="Cambiar a vista de cuadrícula"
                  className={`px-3 py-2 text-sm font-medium rounded-l-lg transition-colors duration-200 ${
                    vistaActual === 'grid'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setVistaActual('lista')}
                  aria-label="Cambiar a vista de lista"
                  className={`px-3 py-2 text-sm font-medium rounded-r-lg transition-colors duration-200 ${
                    vistaActual === 'lista'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <ListBulletIcon className="h-4 w-4" />
                </button>
              </div>
              {tienePermiso('archivos:subir') && (
                <button
                  onClick={() => setMostrarSubida(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                  Subir Archivo
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900">
                <DocumentIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Archivos</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{estadisticas.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900">
                <ArchiveBoxIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Espacio Usado</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatearTamaño(estadisticas.tamañoTotal)}      
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900">
                <ArrowDownTrayIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Descargas Hoy</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{estadisticas.descargasHoy}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-yellow-100 dark:bg-yellow-900">
                <CloudArrowUpIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Subidos Hoy</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{estadisticas.subidosHoy}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <AnimatePresence>
          {mostrarFiltros && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="archivos-filtro-busqueda" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Buscar
                  </label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      id="archivos-filtro-busqueda"
                      name="busqueda"
                      type="text"
                      placeholder="Nombre, descripción, tags..."
                      value={filtros.busqueda}
                      onChange={(e) => setFiltros(prev => ({ ...prev, busqueda: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="archivos-filtro-tipo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo
                  </label>
                  <select
                    id="archivos-filtro-tipo"
                    name="tipo"
                    value={filtros.tipo}
                    onChange={(e) => setFiltros(prev => ({ ...prev, tipo: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Todos los tipos</option>
                    <option value="documento">Documentos</option>
                    <option value="imagen">Imágenes</option>
                    <option value="video">Videos</option>
                    <option value="audio">Audio</option>
                    <option value="archivo">Otros</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="archivos-filtro-categoria" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoría
                  </label>
                  <select
                    id="archivos-filtro-categoria"
                    name="categoria"
                    value={filtros.categoria}
                    onChange={(e) => setFiltros(prev => ({ ...prev, categoria: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Todas las categorías</option>
                    <option value="Manuales">Manuales</option>
                    <option value="Presentaciones">Presentaciones</option>
                    <option value="Reportes">Reportes</option>
                    <option value="Recursos">Recursos</option>
                    <option value="Capacitación">Capacitación</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="archivos-filtro-estado" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estado
                  </label>
                  <select
                    id="archivos-filtro-estado"
                    name="estado"
                    value={filtros.estado}
                    onChange={(e) => setFiltros(prev => ({ ...prev, estado: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Todos los estados</option>
                    <option value="activo">Activo</option>
                    <option value="archivado">Archivado</option>
                    <option value="eliminado">Eliminado</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Acciones masivas */}
        {archivosSeleccionados.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {archivosSeleccionados.length} archivo(s) seleccionado(s)
                </span>
              </div>
              <div className="flex space-x-2">
                <button className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm">
                  Descargar
                </button>
                <button className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm">
                  Archivar
                </button>
                <button className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm">
                  Eliminar
                </button>
                <button
                  onClick={() => setArchivosSeleccionados([])}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Lista/Grid de archivos */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Archivos ({archivosFiltrados.length})
              </h2>
              <div className="flex items-center space-x-4">
                {vistaActual === 'lista' && (
                  <label className="flex items-center">
                    <input
                      id="archivos-seleccionar-todos"
                      name="seleccionarTodos"
                      type="checkbox"
                      checked={archivosSeleccionados.length === archivosPaginados.length && archivosPaginados.length > 0}
                      onChange={manejarSeleccionTodos}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Seleccionar todos</span>
                  </label>
                )}
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Mostrando {indiceInicio + 1}-{Math.min(indiceFin, archivosFiltrados.length)} de {archivosFiltrados.length}
                </div>
              </div>
            </div>
          </div>

          {vistaActual === 'grid' ? (
            // Vista Grid
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <AnimatePresence>
                  {archivosPaginados.map((archivo, index) => (
                    <motion.div
                      key={archivo.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer group"
                      role="button"
                      tabIndex={0}
                      aria-label={`Ver detalles del archivo ${archivo.nombre}`}
                      onClick={() => abrirModal('ver', archivo)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          abrirModal('ver', archivo)
                        }
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          {obtenerIconoTipo(archivo.tipo)}
                          <input
                            id={`archivos-grid-seleccion-${archivo.id}`}
                            name="archivosSeleccionados"
                            aria-label={`Seleccionar archivo ${archivo.nombre}`}
                            type="checkbox"
                            checked={archivosSeleccionados.includes(archivo.id)}
                            onChange={(e) => {
                              e.stopPropagation()
                              manejarSeleccionArchivo(archivo.id)
                            }}
                            className="ml-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                          {tienePermiso('archivos:editar') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                abrirModal('editar', archivo)
                              }}
                              aria-label={`Editar ${archivo.nombre}`}
                              className="p-1 text-blue-600 hover:text-blue-800"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              // Descargar archivo
                            }}
                            aria-label={`Descargar ${archivo.nombre}`}
                            className="p-1 text-green-600 hover:text-green-800"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </button>
                          {tienePermiso('archivos:eliminar') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                abrirModal('eliminar', archivo)
                              }}
                              aria-label={`Eliminar ${archivo.nombre}`}
                              className="p-1 text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {archivo.thumbnail && (
                        <div className="mb-3">
                          <img
                            src={archivo.thumbnail}
                            alt={archivo.nombre}
                            className="w-full h-32 object-cover rounded"
                          />
                        </div>
                      )}
                      
                      <h3 className="font-medium text-gray-900 dark:text-white text-sm mb-2 truncate">
                        {archivo.nombre}
                      </h3>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>{formatearTamaño(archivo.tamaño)}</span>
                          <span>{(archivo.extension || '').toUpperCase()}</span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorEstado(archivo.estado)}`}>
                            {archivo.estado}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {archivo.descargas} descargas
                          </span>
                        </div>
                        
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(archivo.fechaSubida).toLocaleDateString('es-ES')}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ) : (
            // Vista Lista
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      <input
                        id="archivos-tabla-seleccionar-todos"
                        name="seleccionarTodosTabla"
                        type="checkbox"
                        aria-label="Seleccionar todos los archivos visibles"
                        checked={archivosSeleccionados.length === archivosPaginados.length && archivosPaginados.length > 0}
                        onChange={manejarSeleccionTodos}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </th>
                    <th
                      onClick={() => manejarOrdenamiento('nombre')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Archivo</span>
                        {ordenamiento.campo === 'nombre' && (
                          ordenamiento.direccion === 'asc' ? 
                          <ArrowUpIcon className="h-4 w-4" /> : 
                          <ArrowDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th
                      onClick={() => manejarOrdenamiento('tamaño')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Tamaño</span>
                        {ordenamiento.campo === 'tamaño' && (
                          ordenamiento.direccion === 'asc' ? 
                          <ArrowUpIcon className="h-4 w-4" /> : 
                          <ArrowDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Estado
                    </th>
                    <th
                      onClick={() => manejarOrdenamiento('fechaSubida')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Fecha</span>
                        {ordenamiento.campo === 'fechaSubida' && (
                          ordenamiento.direccion === 'asc' ? 
                          <ArrowUpIcon className="h-4 w-4" /> : 
                          <ArrowDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <AnimatePresence>
                    {archivosPaginados.map((archivo, index) => (
                      <motion.tr
                        key={archivo.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            id={`archivos-tabla-seleccion-${archivo.id}`}
                            name="archivosSeleccionadosTabla"
                            aria-label={`Seleccionar archivo ${archivo.nombre}`}
                            type="checkbox"
                            checked={archivosSeleccionados.includes(archivo.id)}
                            onChange={() => manejarSeleccionArchivo(archivo.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 mr-3">
                              {obtenerIconoTipo(archivo.tipo)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {archivo.nombre}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {archivo.categoria}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 dark:text-white">
                            {(archivo.extension || '').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatearTamaño(archivo.tamaño)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorEstado(archivo.estado)}`}>
                            {archivo.estado}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div>{new Date(archivo.fechaSubida).toLocaleDateString('es-ES')}</div>
                          <div className="text-xs">por {archivo.usuario}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => abrirModal('ver', archivo)}
                              aria-label={`Ver ${archivo.nombre}`}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            {tienePermiso('archivos:editar') && (
                              <button
                                onClick={() => abrirModal('editar', archivo)}
                                aria-label={`Editar ${archivo.nombre}`}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button aria-label={`Descargar ${archivo.nombre}`} className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300">
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                            {tienePermiso('archivos:eliminar') && (
                              <button
                                onClick={() => abrirModal('eliminar', archivo)}
                                aria-label={`Eliminar ${archivo.nombre}`}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          {totalPaginas > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
                    disabled={paginaActual === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPaginaActual(prev => Math.min(prev + 1, totalPaginas))}
                    disabled={paginaActual === totalPaginas}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Mostrando <span className="font-medium">{indiceInicio + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(indiceFin, archivosFiltrados.length)}</span> de{' '}
                      <span className="font-medium">{archivosFiltrados.length}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
                        disabled={paginaActual === 1}
                        aria-label="Página anterior"
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pagina) => (
                        <button
                          key={pagina}
                          onClick={() => setPaginaActual(pagina)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pagina === paginaActual
                              ? 'z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 text-blue-600 dark:text-blue-400'
                              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {pagina}
                        </button>
                      ))}
                      <button
                        onClick={() => setPaginaActual(prev => Math.min(prev + 1, totalPaginas))}
                        disabled={paginaActual === totalPaginas}
                        aria-label="Página siguiente"
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de archivo */}
      <ModalArchivo
        isOpen={modalVisible}
        onClose={cerrarModal}
        tipo={tipoModal}
        archivo={archivoSeleccionado}
        onGuardar={manejarGuardarArchivo}
        onEliminar={manejarEliminarArchivo}
        onSubir={manejarSubirArchivo}
      />

      {/* Modal de subida */}
      <ModalArchivo
        isOpen={mostrarSubida}
        onClose={() => setMostrarSubida(false)}
        tipo="subir"
        onSubir={manejarSubirArchivo}
      />
    </div>
  )
}

export default PaginaGestionArchivos
